/**
 * Dashboard-Kachel "Neu registrierte Kunden (Online)".
 *
 * Zeigt die ueber die End-User-App selbst registrierten Bucher eines Tenants
 * im gewaehlten Zeitfenster als scannbare Zeilen-Liste:
 *   - Initialen-Avatar + Bucher-Name
 *   - relativer Registrierungszeitpunkt ("vor 3 Tagen registriert")
 *   - Teilnehmer-Chips (falls vorhanden)
 *   - Lead-/Status-Badge je leadStatus (BOOKED / WITHOUT_ATTENDEES /
 *     WITH_ATTENDEES_NO_BOOKING)
 *   - Aktion "Kontaktieren" -> oeffnet die vorhandene E-Mail-Template-Auswahl
 *
 * Datenschutz: enthaelt Kontaktdaten -> nur fuer Admin/Superuser sichtbar
 * (serverseitig per @Authorized gegated, Array-Level-Gating in
 * DashboardOverview.tsx als UX-Schicht).
 *
 * Backend-Vertrag: useGetOnlineRegisteredBookersQuery (entsteht via build:types).
 */
import React, {FC, memo, useState} from "react";
import {Avatar, Button, Card, Empty, List, Select, Skeleton, Space, Tag, Typography} from "antd";
import {UserAddOutlined} from "@ant-design/icons";
import moment, {Moment} from "moment";
import {Booker, MailDataType, useGetOnlineRegisteredBookersQuery} from "../../../generated/graphql";
import {DashboardConfig} from "../DashboardOverview/DashboardOverview";
import {Link} from "react-router-dom";
import {URL_BOOKERS_DETAILS_ATTENDEES_ID, URL_BOOKERS_DETAILS_ID} from "../../../models/url";
import SendEmailTemplate from "../../common/tableTools/SendEmailTemplate/SendEmailTemplate";
import {timeRanges} from "./timeRanges";
import i18n from "../../../services/i18n";
import css from "./CardOnlineBookers.less";

const {Text} = Typography;
const {Option} = Select;

// Nur diese 4 Zeitfenster werden als Filter angeboten (Teilmenge von timeRanges).
type OnlineBookersTimeRangeKey = "today" | "thisWeek" | "thisMonth" | "all";

type CardOnlineBookersProps = {
    dashboardConfig: DashboardConfig;
    style?: React.CSSProperties;
};

// Minimaler Bucher fuer "Kontaktieren": SendEmailTemplate liest synchron nur
// id/email/firstname/lastname; den Rest laedt das Backend per id nach.
type ContactBooker = Pick<Booker, "id" | "email" | "firstname" | "lastname">;

// Lokale Item-Typen entsprechend dem Backend-Vertrag (getOnlineRegisteredBookers).
// Strukturell kompatibel mit dem generierten Query-Typ -> entkoppelt die
// Render-Logik vom (erst nach build:types vorhandenen) generierten Hook und
// verhindert implizite any-Typen in den map-Callbacks.
type OnlineBookerAttendee = {
    id: string;
    firstname: string;
    lastname: string;
};

type OnlineRegisteredBookerItem = {
    id: string;
    firstname: string;
    lastname: string;
    email?: string | null;
    mobileNumber?: string | null;
    createDate: string;
    attendeesCount: number;
    activeBookingsCount: number;
    leadStatus: string;
    attendees: Array<OnlineBookerAttendee>;
};

const LEAD_STATUS = {
    bookedColor: "green",
    withoutAttendeesColor: "orange",
    withAttendeesNoBookingColor: "gold",
    interestColor: "processing",
};

const getInitials = (firstname: string, lastname: string): string => {
    const first = firstname && firstname.length > 0 ? firstname.charAt(0) : "";
    const last = lastname && lastname.length > 0 ? lastname.charAt(0) : "";
    const initials = `${first}${last}`.toUpperCase();

    return initials.length > 0 ? initials : "?";
};

const renderLeadBadge = (leadStatus: string): React.ReactNode => {
    if (leadStatus === "BOOKED") {
        return <Tag color={LEAD_STATUS.bookedColor}>{i18n.containers.dashboard.CardOnlineBookers.badgeBooked()}</Tag>;
    }
    if (leadStatus === "WITHOUT_ATTENDEES") {
        return <Tag color={LEAD_STATUS.withoutAttendeesColor}>{i18n.containers.dashboard.CardOnlineBookers.badgeWithoutAttendees()}</Tag>;
    }
    if (leadStatus === "INTEREST") {
        return <Tag color={LEAD_STATUS.interestColor}>{i18n.containers.dashboard.CardOnlineBookers.badgeInterest()}</Tag>;
    }
    if (leadStatus === "WITH_ATTENDEES_NO_BOOKING") {
        return <Tag color={LEAD_STATUS.withAttendeesNoBookingColor}>{i18n.containers.dashboard.CardOnlineBookers.badgeWithAttendeesNoBooking()}</Tag>;
    }

    return null;
};

const CardOnlineBookers: FC<CardOnlineBookersProps> = ({dashboardConfig, style}) => {
    const {cardHeight, innerCardHeight} = dashboardConfig;

    const [timeRange, setTimeRange] = useState<OnlineBookersTimeRangeKey>("thisWeek");
    // key zwingt SendEmailTemplate bei jedem Klick zum Remount: autoOpen wirkt
    // nur beim Mount (useState-Initialwert), sonst oeffnet der 2. Klick nichts.
    const [contactBooker, setContactBooker] = useState<{booker: ContactBooker; key: number} | undefined>(undefined);

    // "Alle" sendet keine Datumsgrenzen (undefined) -> Backend filtert nicht.
    const range: [Moment, Moment] | undefined = timeRange === "all" ? undefined : timeRanges[timeRange];

    const {data, loading} = useGetOnlineRegisteredBookersQuery({
        variables: {
            startDateTime: range ? range[0].toISOString() : undefined,
            endDateTime: range ? range[1].toISOString() : undefined,
            limit: 50,
            offset: 0,
        },
        fetchPolicy: "cache-and-network",
    });

    const result = data && data.getOnlineRegisteredBookers;
    // Cast auf den lokalen Item-Typ: der generierte Query-Typ ist strukturell
    // kompatibel; der Cast haelt die Render-Logik unabhaengig von Maybe<>-/
    // __typename-Details des Codegen-Outputs.
    const items = ((result && result.items) || []) as Array<OnlineRegisteredBookerItem>;
    const total = (result && result.total) || 0;

    const headStyle: React.CSSProperties = {
        // Hellerer Kopf, optisch konsistent zu den anderen Kacheln.
    };

    const title = (
        <Space size={8} style={{display: "flex", justifyContent: "space-between", width: "100%"}}>
            <span style={{fontSize: "1.1em"}}>
                <UserAddOutlined style={{marginRight: 6}} />
                {i18n.containers.dashboard.CardOnlineBookers.cardTitle()}
                {!loading && (
                    <Text type="secondary" style={{marginLeft: 8, fontSize: "0.85em"}}>
                        {i18n.containers.dashboard.CardOnlineBookers.total({count: String(total)})}
                    </Text>
                )}
            </span>
            {/* getPopupContainer an document.body (Ant-Default): trigger.parentElement
                wuerde das Dropdown im overflow:hidden-Karten-Header abschneiden. */}
            <Select<OnlineBookersTimeRangeKey>
                size="small"
                value={timeRange}
                onChange={(value) => setTimeRange(value)}
                style={{width: 130}}
                getPopupContainer={() => document.body}
            >
                <Option value="today">{i18n.containers.dashboard.CardOnlineBookers.timeRangeToday()}</Option>
                <Option value="thisWeek">{i18n.containers.dashboard.CardOnlineBookers.timeRangeThisWeek()}</Option>
                <Option value="thisMonth">{i18n.containers.dashboard.CardOnlineBookers.timeRangeThisMonth()}</Option>
                <Option value="all">{i18n.containers.dashboard.CardOnlineBookers.timeRangeAll()}</Option>
            </Select>
        </Space>
    );

    const renderItem = (item: OnlineRegisteredBookerItem): React.ReactNode => {
        const fullName = `${item.firstname} ${item.lastname}`;
        const attendees = item.attendees || [];

        return (
            <List.Item
                className={css.bookerRow}
                key={item.id}
                actions={[
                    <Button
                        key="contact"
                        type="link"
                        size="small"
                        onClick={() =>
                            setContactBooker((prev) => ({
                                booker: {
                                    id: item.id,
                                    email: item.email,
                                    firstname: item.firstname,
                                    lastname: item.lastname,
                                },
                                key: prev ? prev.key + 1 : 1,
                            }))
                        }
                    >
                        {i18n.containers.dashboard.CardOnlineBookers.contact()}
                    </Button>,
                ]}
            >
                <List.Item.Meta
                    avatar={<Avatar className={css.avatar} size="small">{getInitials(item.firstname, item.lastname)}</Avatar>}
                    title={
                        <Link to={URL_BOOKERS_DETAILS_ID.replace(":id", item.id)} className={css.bookerName} title={fullName}>
                            {fullName}
                        </Link>
                    }
                    description={
                        <div className={css.bookerSubLine}>
                            <Text type="secondary" className={css.registeredAt}>
                                {`${moment(item.createDate).fromNow()} ${i18n.containers.dashboard.CardOnlineBookers.registeredSuffix()}`}
                            </Text>
                            <div className={css.chipRow}>
                                {attendees.length > 0 ? (
                                    attendees.map((attendee) => (
                                        <Link
                                            key={attendee.id}
                                            to={URL_BOOKERS_DETAILS_ATTENDEES_ID.replace(":id", item.id)}
                                            title={`${attendee.firstname} ${attendee.lastname}`}
                                        >
                                            <Tag className={css.chip}>{`${attendee.firstname} ${attendee.lastname}`}</Tag>
                                        </Link>
                                    ))
                                ) : null}
                                {renderLeadBadge(item.leadStatus)}
                            </div>
                        </div>
                    }
                />
            </List.Item>
        );
    };

    const renderBody = (): React.ReactNode => {
        if (loading && items.length === 0) {
            return <Skeleton active paragraph={{rows: 5}} />;
        }

        if (items.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={i18n.containers.dashboard.CardOnlineBookers.empty()}
                />
            );
        }

        return (
            <List
                className={css.bookerList}
                size="small"
                dataSource={items}
                renderItem={renderItem}
            />
        );
    };

    return (
        <Card
            size="small"
            title={title}
            hoverable
            headStyle={headStyle}
            bodyStyle={{
                height: innerCardHeight,
                overflowY: "auto",
                padding: 8,
            }}
            style={{
                ...style,
                borderRadius: 15,
                overflow: "hidden",
                height: cardHeight,
            }}
        >
            <div className={css.scrollContainer}>{renderBody()}</div>
            {contactBooker && (
                <SendEmailTemplate
                    key={contactBooker.key}
                    autoOpen
                    input={{
                        mailDataType: MailDataType.Booker,
                        selection: [contactBooker.booker as unknown as Booker],
                    }}
                />
            )}
        </Card>
    );
};

export default memo(CardOnlineBookers);
